import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Download, Plus, RefreshCw, Trash2 } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import DatePicker from '../ui/DatePicker';
import Table from '../ui/Table';
import Modal from '../ui/Modal';
import CalendarCell from '../ui/CalendarCell';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { clean } from '../shell/copy';

// Admin → PTO tracker, on src/ui (DESIGN rev 2.1 §3.1: Table for the lists, Calendar cell for who's out).
// Admin/Manager view of employee PTO: hire-date setup + backfill, status management, live balances, manual
// adjustments, the approval and change-request queues, company holidays and the CSV reports. Balances are computed
// server-side by summing the ledger; this UI never stores or derives a balance itself. Same calls as before.

const STATUSES = ['Active', 'Inactive', 'LOA'];
const STATE_TONE = { Pending: 'info', Approved: 'ok', Taken: 'ok', Denied: 'danger' };

// Balances travel as signed half-days (1 day = 2 half-days).
function fmtBalance(halfDays) {
  if (halfDays == null) return '';
  const days = halfDays / 2;
  return `${days} day${days === 1 ? '' : 's'}`;
}
const errOf = (e, fallback) => clean(e?.response?.data?.error) || fallback;
const none = <span className="ui-text-3">None</span>;
const stateBadge = s => (STATE_TONE[s] ? <Badge tone={STATE_TONE[s]}>{s}</Badge> : <span className="ui-text-3">{s}</span>);
const range = (a, b, half) => `${a}${b && b !== a ? ` to ${b}` : ''}${half ? ', half day' : ''}`;
const isHalfDays = n => Number.isInteger(n * 2);

// Two lines in one cell: a value and a quieter line under it.
function Stacked({ main, sub }) {
  return (
    <span className="ui-stack ui-stack--tight ui-cell-lines">
      <span className="ui-clip" title={typeof main === 'string' ? main : undefined}>{main}</span>
      {sub ? <span className="ui-clip ui-text-3" title={typeof sub === 'string' ? sub : undefined}>{sub}</span> : null}
    </span>
  );
}

// ── Adjustment modal ──────────────────────────────────────────────────────────
// Two modes:
//   'adjust': book a signed +/- delta (grant or deduct days).
//   'set':    SET the available balance to an exact number; the server books the correcting entry
//             (target minus current). Used for mid-year starts where days were already used and accrual
//             does not reflect it.
function AdjustModal({ employee, onClose, onSaved }) {
  const [mode, setMode] = useState('adjust');
  const [days, setDays] = useState('');       // entered in DAYS, converted to half-days
  const [reason, setReason] = useState('');
  const [docReference, setDocReference] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async (e) => {
    e?.preventDefault();
    setErr('');
    const daysNum = Number(days);
    if (days === '' || Number.isNaN(daysNum)) { setErr(mode === 'set' ? 'Enter the balance in days.' : 'Enter a number of days.'); return; }
    if (!isHalfDays(daysNum)) { setErr(`Days must be in half-day steps (e.g. 1, 0.5${mode === 'set' ? '' : ', -2'}).`); return; }
    if (mode === 'adjust' && daysNum === 0) { setErr('Enter a number of days other than zero (negative deducts).'); return; }
    if (mode === 'set' && daysNum < 0) { setErr('A balance cannot be negative.'); return; }
    if (!reason.trim()) { setErr('A reason is required.'); return; }
    setSaving(true);
    try {
      if (mode === 'set') {
        await api.put(`/api/pto/employees/${employee.teamMemberId}/balance`, {
          targetHalfDays: Math.round(daysNum * 2),
          reason: reason.trim(),
          docReference: docReference.trim() || undefined,
        });
      } else {
        await api.post(`/api/pto/employees/${employee.teamMemberId}/adjustment`, {
          halfDays: Math.round(daysNum * 2),
          reason: reason.trim(),
          docReference: docReference.trim() || undefined,
        });
      }
      onSaved(mode === 'set' ? 'Balance set.' : 'Adjustment recorded.');
      onClose();
    } catch (e2) {
      setErr(errOf(e2, 'The change did not save. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  const curDays = employee.balanceHalfDays == null ? null : employee.balanceHalfDays / 2;
  const targetNum = Number(days);
  const previewValid = mode === 'set' && days !== '' && !Number.isNaN(targetNum) && isHalfDays(targetNum) && targetNum >= 0 && curDays != null;
  const correction = previewValid ? targetNum - curDays : 0;
  const switchMode = m => { setMode(m); setDays(''); setErr(''); };

  return (
    <Modal open onClose={onClose} title={`${mode === 'set' ? 'Set PTO balance' : 'Adjust PTO'}: ${clean(employee.name)}`}
      footer={<>
        <Button variant="quiet" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={saving} onClick={handleSave}>{mode === 'set' ? 'Set balance' : 'Record adjustment'}</Button>
      </>}>
      <form className="ui-stack" onSubmit={handleSave}>
        <div className="ui-seg" role="group" aria-label="Kind of change">
          <Button variant="quiet" aria-pressed={mode === 'adjust'} onClick={() => switchMode('adjust')}>Adjust by (+/-)</Button>
          <Button variant="quiet" aria-pressed={mode === 'set'} onClick={() => switchMode('set')}>Set to exact</Button>
        </div>
        <p className="ui-p ui-dense">Current balance: <span className="ui-num">{fmtBalance(employee.balanceHalfDays) || 'None'}</span></p>
        {mode === 'set' ? (
          <Input label="Set the available balance to (days)" type="number" step="0.5" min="0" value={days} onChange={e => setDays(e.target.value)} placeholder="e.g. 4.5" data-autofocus
            hint={previewValid ? (correction === 0
              ? 'Already at this balance. No change will be booked.'
              : `The system books a ${correction > 0 ? '+' : ''}${correction} day correction. New balance: ${targetNum} days.`) : undefined} />
        ) : (
          <Input label="Days (+ grants, - deducts)" type="number" step="0.5" value={days} onChange={e => setDays(e.target.value)} placeholder="e.g. 2 or -1.5" data-autofocus />
        )}
        <Input label="Reason" value={reason} onChange={e => setReason(e.target.value)}
          placeholder={mode === 'set' ? 'e.g. Mid-year start, 5.5 days already used' : 'Why this adjustment is being made'} />
        <Input label="Doc reference (optional)" value={docReference} onChange={e => setDocReference(e.target.value)} placeholder="Link or note if this differs from policy" />
        {err && <p className="ui-p ui-field__error" role="alert">{err}</p>}
      </form>
    </Modal>
  );
}

// A one- or two-field reason dialog: replaces the old window.prompt chains.
function ReasonModal({ spec, onClose }) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    const errs = {};
    for (const f of spec.fields) { const msg = f.validate(values[f.key] ?? ''); if (msg) errs[f.key] = msg; }
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    const ok = await spec.onSubmit(values);
    setSaving(false);
    if (ok) onClose();
  };
  return (
    <Modal open onClose={onClose} title={spec.title}
      footer={<>
        <Button variant="quiet" onClick={onClose}>Cancel</Button>
        <Button variant={spec.danger ? 'danger' : 'primary'} loading={saving} onClick={submit}>{spec.submitLabel}</Button>
      </>}>
      <form className="ui-stack" onSubmit={submit}>
        {spec.body && <p className="ui-p ui-dense ui-text-2">{spec.body}</p>}
        {spec.fields.map((f, i) => {
          const common = { label: f.label, value: values[f.key] ?? '', error: errors[f.key], 'data-autofocus': i === 0 ? true : undefined,
            onChange: e => { const v = e.target.value; setValues(s => ({ ...s, [f.key]: v })); setErrors(s => ({ ...s, [f.key]: null })); } };
          return f.type === 'number'
            ? <Input key={f.key} type="number" step="0.5" placeholder={f.placeholder} {...common} />
            : <Textarea key={f.key} rows={2} placeholder={f.placeholder} {...common} />;
        })}
      </form>
    </Modal>
  );
}

const required = msg => v => (String(v).trim() ? null : msg);

// ── Employees view ────────────────────────────────────────────────────────────
function EmployeesView() {
  const [employees, setEmployees] = useState([]);
  const [state, setState] = useState('loading');
  const [loadErr, setLoadErr] = useState('');
  const [editingHire, setEditingHire] = useState(null); // teamMemberId being edited
  const [hireDraft, setHireDraft] = useState('');
  const [hireErr, setHireErr] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const confirm = useConfirm();
  const toast = useToast();

  const load = useCallback(async () => {
    setState(s => (s === 'data' ? s : 'loading'));
    try {
      const { data } = await api.get('/api/pto/employees');
      setEmployees(data.employees || []);
      setState('data');
    } catch (e) {
      setLoadErr(errOf(e, 'The PTO employees did not load.'));
      setState('error');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startHire = (emp) => { setEditingHire(emp.teamMemberId); setHireDraft(emp.hireDate || ''); setHireErr(''); };
  const cancelHire = () => { setEditingHire(null); setHireDraft(''); setHireErr(''); };

  const saveHireDate = async (emp) => {
    if (!hireDraft || !/^\d{4}-\d{2}-\d{2}$/.test(hireDraft)) { setHireErr('Enter a valid hire date.'); return; }
    setBusyId(emp.teamMemberId); setHireErr('');
    try {
      await api.put(`/api/pto/employees/${emp.teamMemberId}/hire-date`, { hireDate: hireDraft });
      cancelHire();
      toast(`Hire date set for ${clean(emp.name)}.`);
      await load();
    } catch (e) {
      setHireErr(errOf(e, 'The hire date did not save.'));
    } finally {
      setBusyId(null);
    }
  };

  const changeStatus = async (emp, status) => {
    if (status === emp.status) return;
    if (status === 'Inactive') {
      const ok = await confirm({
        title: `Mark ${clean(emp.name)} inactive?`,
        body: 'This forfeits their current-year balance. There is no payout.',
        confirmLabel: 'Mark inactive', destructive: true,
      });
      if (!ok) return;
    }
    setBusyId(emp.teamMemberId);
    try {
      await api.put(`/api/pto/employees/${emp.teamMemberId}/status`, { status });
      toast(`${clean(emp.name)} is now ${status === 'LOA' ? 'on leave (LOA)' : status.toLowerCase()}.`);
      await load();
    } catch (e) {
      toast(errOf(e, 'The status did not change. Try again.'), { tone: 'danger' });
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    { key: 'name', label: 'Name', width: '24%', render: emp => <Stacked main={clean(emp.name)} sub={emp.email} /> },
    { key: 'hire', label: 'Hire date', width: 320, render: emp => {
      const busy = busyId === emp.teamMemberId;
      if (editingHire === emp.teamMemberId) {
        return (
          <span className="ui-stack ui-stack--tight">
            <span className="ui-cluster">
              <DatePicker aria-label={`Hire date for ${clean(emp.name)}`} value={hireDraft} onChange={e => setHireDraft(e.target.value)} data-autofocus />
              <Button variant="primary" loading={busy} onClick={() => saveHireDate(emp)}>Save</Button>
              <Button variant="quiet" onClick={cancelHire}>Cancel</Button>
            </span>
            {hireErr && <span className="ui-field__error" role="alert">{hireErr}</span>}
          </span>
        );
      }
      return emp.hireDate ? (
        <span className="ui-cluster">
          <span className="ui-num">{emp.hireDate}</span>
          <Button variant="quiet" onClick={() => startHire(emp)}>Edit</Button>
        </span>
      ) : (
        <span className="ui-cluster">
          <Badge tone="warn">Hire date needed</Badge>
          <Button variant="quiet" onClick={() => startHire(emp)}>Set</Button>
        </span>
      );
    } },
    { key: 'status', label: 'Status', width: 248, render: emp => (emp.status ? (
      <span className="ui-cluster">
        <Select aria-label={`Status for ${clean(emp.name)}`} value={STATUSES.includes(emp.status) ? emp.status : ''}
          disabled={busyId === emp.teamMemberId || !emp.hireDate} onChange={e => changeStatus(emp, e.target.value)}
          options={[...(!STATUSES.includes(emp.status) ? [{ value: '', label: emp.status === 'First90' ? 'First 90 days' : emp.status }] : []), ...STATUSES]} />
        {emp.status === 'First90' && <Badge tone="info">First 90 days</Badge>}
      </span>
    ) : none) },
    { key: 'balance', label: 'Balance', render: emp => (emp.hireDate ? <span className="ui-num">{fmtBalance(emp.balanceHalfDays)}</span> : none) },
    { key: 'actions', label: <span className="ui-sr">Actions</span>, width: 104, render: emp => (
      <Button variant="quiet" disabled={!emp.hireDate || busyId === emp.teamMemberId} onClick={() => setAdjustFor(emp)}>Adjust</Button>
    ) },
  ];

  return (
    <section className="ui-section" aria-label="Employees">
      <div className="ui-toolbar">
        <p className="ui-section__note">Set a hire date to enroll an employee. Balances accrue automatically at the 90-day mark and reset on each work anniversary.</p>
        <span className="ui-spacer" />
        <Button variant="quiet" icon={RefreshCw} onClick={load}>Refresh</Button>
      </div>
      <div className="ui-region">
        <Table caption="PTO employees" columns={columns} rows={employees} rowKey={e => e.teamMemberId} state={state}
          error={{ title: 'Employees did not load', message: loadErr, onRetry: load }}
          empty={{ message: 'No internal staff found. Internal team members appear here once they are added in Team members.' }} />
      </div>
      {adjustFor && <AdjustModal employee={adjustFor} onClose={() => setAdjustFor(null)} onSaved={msg => { toast(msg); load(); }} />}
    </section>
  );
}

// ── Approval queue ────────────────────────────────────────────────────────────
function ApprovalQueue() {
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [state, setState] = useState('loading');
  const [loadErr, setLoadErr] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [reason, setReason] = useState(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setState('loading');
    try {
      const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const { data } = await api.get(`/api/pto/requests${q}`);
      setRequests(data.requests || []);
      setState('data');
    } catch (e) { setLoadErr(errOf(e, 'The requests did not load.')); setState('error'); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    setBusyId(r.id);
    try { await api.post(`/api/pto/requests/${r.id}/approve`); toast(`Approved for ${clean(r.memberName) || 'the employee'}.`); await load(); }
    catch (e) { toast(errOf(e, 'The approval did not go through. Try again.'), { tone: 'danger' }); }
    finally { setBusyId(null); }
  };
  const deny = (r) => setReason({
    title: `Deny ${r.memberName ? `${clean(r.memberName)}'s` : 'this'} request?`,
    body: range(r.startDate, r.endDate, r.halfStart || r.halfEnd),
    submitLabel: 'Deny request', danger: true,
    fields: [{ key: 'reason', label: 'Reason (required)', validate: required('A denial reason is required.') }],
    onSubmit: async ({ reason: text }) => {
      setBusyId(r.id);
      try { await api.post(`/api/pto/requests/${r.id}/deny`, { denialReason: text.trim() }); toast('Request denied.'); await load(); return true; }
      catch (e) { toast(errOf(e, 'The denial did not go through. Try again.'), { tone: 'danger' }); return false; }
      finally { setBusyId(null); }
    },
  });

  const columns = [
    { key: 'employee', label: 'Employee', width: '20%', render: r => <Stacked main={r.memberName ? clean(r.memberName) : 'Unknown'} sub={r.memberEmail} /> },
    { key: 'dates', label: 'Dates', width: '24%', render: r => (
      <span className="ui-cluster">
        <span className="ui-num">{range(r.startDate, r.endDate, r.halfStart || r.halfEnd)}</span>
        {r.pastDated && <Badge tone="warn">Past date</Badge>}
      </span>
    ) },
    { key: 'days', label: 'Days', width: 96, render: r => <span className="ui-num">{fmtBalance(r.totalHalfDays)}</span> },
    { key: 'reason', label: 'Reason', render: r => <Stacked main={clean(r.reason)} sub={r.note ? clean(r.note) : null} /> },
    { key: 'status', label: 'State', width: 160, render: r => <Stacked main={stateBadge(r.status)} sub={r.status === 'Denied' && r.denialReason ? clean(r.denialReason) : null} /> },
    { key: 'actions', label: <span className="ui-sr">Actions</span>, width: 184, render: r => (r.status === 'Pending' ? (
      <span className="ui-cluster">
        <Button variant="secondary" disabled={busyId === r.id} onClick={() => approve(r)}>Approve</Button>
        <Button variant="quiet" disabled={busyId === r.id} onClick={() => deny(r)}>Deny</Button>
      </span>
    ) : null) },
  ];

  return (
    <section className="ui-section" aria-label="Approval queue">
      <div className="ui-toolbar">
        <Select aria-label="Request state" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          options={['Pending', 'Approved', 'Denied', 'Cancelled', 'Taken', ''].map(s => ({ value: s, label: s || 'All' }))} />
        <span className="ui-spacer" />
        <Button variant="quiet" icon={RefreshCw} onClick={load}>Refresh</Button>
      </div>
      <div className="ui-region">
        <Table caption="PTO requests" columns={columns} rows={requests} state={state}
          error={{ title: 'Requests did not load', message: loadErr, onRetry: load }}
          empty={{ message: statusFilter === 'Pending' ? 'No requests are waiting for approval.' : statusFilter ? 'No requests in this state. Pick All to see every request.' : 'No time-off requests yet. Employees send them from Time off.' }} />
      </div>
      {reason && <ReasonModal spec={reason} onClose={() => setReason(null)} />}
    </section>
  );
}

// ── Holiday calendar ──────────────────────────────────────────────────────────
function HolidayCalendar() {
  const [holidays, setHolidays] = useState([]);
  const [state, setState] = useState('loading');
  const [loadErr, setLoadErr] = useState('');
  const [form, setForm] = useState({ holidayDate: '', name: '' });
  const [formErr, setFormErr] = useState('');
  const confirm = useConfirm();
  const toast = useToast();

  const load = useCallback(async () => {
    setState(s => (s === 'data' ? s : 'loading'));
    try { const { data } = await api.get('/api/pto/holidays'); setHolidays(data.holidays || []); setState('data'); }
    catch (e) { setLoadErr(errOf(e, 'The holidays did not load.')); setState('error'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e?.preventDefault();
    if (!form.holidayDate || !form.name.trim()) { setFormErr('Date and name are required.'); return; }
    setFormErr('');
    try {
      await api.post('/api/pto/holidays', { holidayDate: form.holidayDate, name: form.name.trim() });
      toast(`${form.name.trim()} added.`);
      setForm({ holidayDate: '', name: '' });
      await load();
    } catch (e2) { setFormErr(errOf(e2, 'The holiday did not save. Try again.')); }
  };
  const remove = async (h) => {
    const ok = await confirm({ title: `Remove ${clean(h.name)}?`, body: `${h.holidayDate} becomes a normal working day for PTO counts.`, confirmLabel: 'Remove holiday', destructive: true });
    if (!ok) return;
    try { await api.delete(`/api/pto/holidays/${h.id}`); toast(`${clean(h.name)} removed.`); await load(); }
    catch (e) { toast(errOf(e, 'The holiday did not remove. Try again.'), { tone: 'danger' }); }
  };

  const columns = [
    { key: 'holidayDate', label: 'Date', width: 160, render: h => <span className="ui-num">{h.holidayDate}</span> },
    { key: 'name', label: 'Name', render: h => clean(h.name) },
    { key: 'actions', label: <span className="ui-sr">Actions</span>, width: 64, render: h => <Button variant="quiet" iconOnly icon={Trash2} label={`Remove ${clean(h.name)}`} onClick={() => remove(h)} /> },
  ];

  return (
    <section className="ui-section" aria-label="Holiday calendar">
      <form className="ui-toolbar ui-toolbar--end" onSubmit={add}>
        <DatePicker label="Date" value={form.holidayDate} onChange={e => setForm(f => ({ ...f, holidayDate: e.target.value }))} />
        <Input label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Thanksgiving" />
        <Button type="submit" variant="secondary" icon={Plus}>Add holiday</Button>
        <span className="ui-spacer" />
        <Button variant="quiet" icon={RefreshCw} onClick={load}>Refresh</Button>
      </form>
      {formErr && <p className="ui-p ui-field__error" role="alert">{formErr}</p>}
      <div className="ui-region">
        <Table caption="Company holidays" columns={columns} rows={holidays} state={state}
          error={{ title: 'Holidays did not load', message: loadErr, onRetry: load }}
          empty={{ message: 'No company holidays yet. Add them above so PTO counts skip those days.' }} />
      </div>
    </section>
  );
}

// ── Month calendar ("who's out") ─────────────────────────────────────────────
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ymdUTC(d) { return d.toISOString().slice(0, 10); }
function addUTCDays(d, n) { const x = new Date(d); x.setUTCDate(x.getUTCDate() + n); return x; }

function CalendarView() {
  // Cursor: first of the displayed month (UTC).
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getUTCFullYear(), month: now.getUTCMonth() });
  const [data, setData] = useState({ requests: [], holidays: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // 6-week grid starting on the Sunday on/before the 1st.
  const first = new Date(Date.UTC(cursor.year, cursor.month, 1));
  const gridStart = addUTCDays(first, -first.getUTCDay());
  const cells = Array.from({ length: 42 }, (_, i) => addUTCDays(gridStart, i));

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const start = ymdUTC(cells[0]);
      const end = ymdUTC(cells[41]);
      const { data: d } = await api.get(`/api/pto/calendar?start=${start}&end=${end}`);
      setData({ requests: d.requests || [], holidays: d.holidays || [] });
    } catch (e) {
      setErr(errOf(e, 'The calendar did not load.'));
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor.year, cursor.month]);
  useEffect(() => { load(); }, [load]);

  const holidayByDate = {};
  data.holidays.forEach(h => { holidayByDate[h.holidayDate] = h.name; });

  // Which people are off on a given weekday (weekends + holidays excluded, to match how PTO is actually counted).
  function entriesFor(dstr, isWeekend) {
    if (isWeekend || holidayByDate[dstr]) return [];
    return data.requests
      .filter(r => dstr >= r.startDate && dstr <= r.endDate)
      .map(r => ({ ...r, half: (dstr === r.startDate && r.halfStart) || (dstr === r.endDate && r.halfEnd) }));
  }

  const step = (delta) => setCursor(c => {
    const m = c.month + delta;
    return { year: c.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
  });
  const goToday = () => setCursor({ year: now.getUTCFullYear(), month: now.getUTCMonth() });
  const todayStr = ymdUTC(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())));

  return (
    <section className="ui-section" aria-label="Who's out">
      <div className="ui-toolbar">
        <Button variant="quiet" iconOnly icon={ChevronLeft} label="Previous month" onClick={() => step(-1)} />
        <span className="ui-heading" aria-live="polite">{MONTHS[cursor.month]} {cursor.year}</span>
        <Button variant="quiet" iconOnly icon={ChevronRight} label="Next month" onClick={() => step(1)} />
        <Button variant="quiet" onClick={goToday}>Today</Button>
        <span className="ui-spacer" />
        <span className="ui-dense ui-text-3">Approved and taken days show the first name. Pending days say pending. Hover a name for the details.</span>
        <Button variant="quiet" icon={RefreshCw} loading={loading} onClick={load}>Refresh</Button>
      </div>
      {err && <p className="ui-p ui-field__error" role="alert">{err}</p>}
      <div className="ui-region">
        <div className="ui-month">
          {DOW.map(d => <div key={d} className="ui-month__dow" aria-hidden="true">{d}</div>)}
          {cells.map(cell => {
            const dstr = ymdUTC(cell);
            const dow = cell.getUTCDay();
            const holiday = holidayByDate[dstr];
            const entries = entriesFor(dstr, dow === 0 || dow === 6).map(e => ({ ...e, label: e.memberName }));
            return (
              <CalendarCell key={dstr} date={dstr} outside={cell.getUTCMonth() !== cursor.month} today={dstr === todayStr} items={entries} max={4}
                action={holiday ? <span className="ui-cal__item ui-text-3" title={clean(holiday)}>{clean(holiday)}</span> : undefined}
                renderItem={e => (
                  <span className="ui-cal__item" title={`${clean(e.memberName)}: ${clean(e.reason)} (${e.status})${e.half ? ', half day' : ''}`}>
                    {clean(e.memberName).split(' ')[0]}{e.half ? ', half' : ''}{e.status === 'Pending' ? <span className="ui-text-3"> pending</span> : null}
                  </span>
                )} />
            );
          })}
        </div>
      </div>
      {!loading && !err && data.requests.length === 0 && <p className="ui-section__note">No time off scheduled this month.</p>}
    </section>
  );
}

// ── Change requests (B3) ──────────────────────────────────────────────────────
function ChangeRequestsView() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [state, setState] = useState('loading');
  const [loadErr, setLoadErr] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [reason, setReason] = useState(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setState('loading');
    try {
      const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const { data } = await api.get(`/api/pto/change-requests${q}`);
      setItems(data.changeRequests || []);
      setState('data');
    } catch (e) { setLoadErr(errOf(e, 'The change requests did not load.')); setState('error'); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (c, body, okMsg) => {
    setBusyId(c.id);
    try { await api.post(`/api/pto/change-requests/${c.id}/resolve`, body); toast(okMsg); await load(); return true; }
    catch (e) { toast(errOf(e, 'The change request did not resolve. Try again.'), { tone: 'danger' }); return false; }
    finally { setBusyId(null); }
  };
  const approve = (c) => setReason({
    title: `Approve the change for ${clean(c.memberName)}`,
    body: clean(c.description),
    submitLabel: 'Approve change',
    fields: [
      { key: 'days', type: 'number', label: 'Balance adjustment in days (+ credits, - deducts, half-day steps)', placeholder: 'e.g. 1 or -0.5',
        validate: v => { const n = Number(v); return (!String(v).trim() || Number.isNaN(n) || n === 0 || !isHalfDays(n)) ? 'Enter a number of days other than zero, in half-day steps.' : null; } },
      { key: 'reason', label: 'Reason for this adjustment (required)', validate: required('A reason is required.') },
    ],
    onSubmit: ({ days, reason: text }) => resolve(c, { decision: 'Approved', halfDays: Math.round(Number(days) * 2), reason: text.trim() }, 'Change approved.'),
  });
  const deny = (c) => setReason({
    title: `Deny the change for ${clean(c.memberName)}?`,
    body: clean(c.description),
    submitLabel: 'Deny change', danger: true,
    fields: [{ key: 'note', label: 'Resolution note (required)', validate: required('A resolution note is required.') }],
    onSubmit: ({ note }) => resolve(c, { decision: 'Denied', resolutionNote: note.trim() }, 'Change denied.'),
  });

  const columns = [
    { key: 'employee', label: 'Employee', width: '20%', render: c => <Stacked main={clean(c.memberName)} sub={c.memberEmail} /> },
    { key: 'original', label: 'Original PTO', width: '26%', render: c => (c.request
      ? <span className="ui-num" title={c.request.status}>{`${range(c.request.startDate, c.request.endDate)}, ${fmtBalance(c.request.totalHalfDays)}, ${c.request.status}`}</span>
      : none) },
    { key: 'change', label: 'Requested change', render: c => <Stacked main={clean(c.description)} sub={clean(c.reason)} /> },
    { key: 'status', label: 'State', width: 160, render: c => <Stacked main={stateBadge(c.status)} sub={c.resolutionNote ? clean(c.resolutionNote) : null} /> },
    { key: 'actions', label: <span className="ui-sr">Actions</span>, width: 184, render: c => (c.status === 'Pending' ? (
      <span className="ui-cluster">
        <Button variant="secondary" disabled={busyId === c.id} onClick={() => approve(c)}>Approve</Button>
        <Button variant="quiet" disabled={busyId === c.id} onClick={() => deny(c)}>Deny</Button>
      </span>
    ) : null) },
  ];

  return (
    <section className="ui-section" aria-label="Change requests">
      <div className="ui-toolbar">
        <Select aria-label="Change request state" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          options={['Pending', 'Approved', 'Denied', ''].map(s => ({ value: s, label: s || 'All' }))} />
        <span className="ui-spacer" />
        <Button variant="quiet" icon={RefreshCw} onClick={load}>Refresh</Button>
      </div>
      <div className="ui-region">
        <Table caption="PTO change requests" columns={columns} rows={items} state={state}
          error={{ title: 'Change requests did not load', message: loadErr, onRetry: load }}
          empty={{ message: statusFilter === 'Pending' ? 'No change requests are waiting.' : statusFilter ? 'No change requests in this state. Pick All to see every one.' : 'No change requests yet. Employees file them from Time off for days already taken.' }} />
      </div>
      {reason && <ReasonModal spec={reason} onClose={() => setReason(null)} />}
    </section>
  );
}

// ── Reports (B2) ──────────────────────────────────────────────────────────────
function ReportsView() {
  const [busy, setBusy] = useState('');
  const [reqFilters, setReqFilters] = useState({ from: '', to: '', status: '' });
  const toast = useToast();

  const download = async (path, filename, key) => {
    setBusy(key);
    try {
      const res = await api.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast(`${filename} did not download. Try again.`, { tone: 'danger' }); }
    finally { setBusy(''); }
  };

  const reqQuery = () => {
    const p = new URLSearchParams();
    if (reqFilters.from) p.set('from', reqFilters.from);
    if (reqFilters.to) p.set('to', reqFilters.to);
    if (reqFilters.status) p.set('status', reqFilters.status);
    const s = p.toString();
    return `/api/pto/reports/requests.csv${s ? '?' + s : ''}`;
  };

  return (
    <div className="ui-stack ui-stack--loose">
      <section className="ui-section" aria-labelledby="pto-r-bal">
        <h2 className="ui-section__title" id="pto-r-bal">Balances</h2>
        <p className="ui-section__note">Every enrolled member: hire date, status, benefit year and every balance bucket, in days.</p>
        <div><Button variant="secondary" icon={Download} loading={busy === 'bal'} onClick={() => download('/api/pto/reports/balances.csv', 'pto-balances.csv', 'bal')}>Download balances.csv</Button></div>
      </section>

      <section className="ui-section" aria-labelledby="pto-r-req">
        <h2 className="ui-section__title" id="pto-r-req">Requests</h2>
        <p className="ui-section__note">The request log, filtered by start-date range and state if you set them.</p>
        <div className="ui-toolbar ui-toolbar--end">
          <DatePicker label="From" value={reqFilters.from} onChange={e => setReqFilters(f => ({ ...f, from: e.target.value }))} />
          <DatePicker label="To" value={reqFilters.to} onChange={e => setReqFilters(f => ({ ...f, to: e.target.value }))} />
          <Select label="State" value={reqFilters.status} onChange={e => setReqFilters(f => ({ ...f, status: e.target.value }))}
            options={['', 'Pending', 'Approved', 'Denied', 'Cancelled', 'Taken'].map(s => ({ value: s, label: s || 'All' }))} />
          <Button variant="secondary" icon={Download} loading={busy === 'req'} onClick={() => download(reqQuery(), 'pto-requests.csv', 'req')}>Download requests.csv</Button>
        </div>
      </section>

      <section className="ui-section" aria-labelledby="pto-r-exp">
        <h2 className="ui-section__title" id="pto-r-exp">Expiring balances</h2>
        <p className="ui-section__note">Active members with unused PTO, soonest expiry first.</p>
        <div><Button variant="secondary" icon={Download} loading={busy === 'exp'} onClick={() => download('/api/pto/reports/expiring.csv', 'pto-expiring.csv', 'exp')}>Download expiring.csv</Button></div>
      </section>
    </div>
  );
}

// ── View switcher ─────────────────────────────────────────────────────────────
const VIEWS = [
  { key: 'employees', label: 'Employees' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'approvals', label: 'Approval queue' },
  { key: 'changes', label: 'Change requests' },
  { key: 'holidays', label: 'Holiday calendar' },
  { key: 'reports', label: 'Reports' },
];

export default function PtoTracker() {
  const [view, setView] = useState('employees');
  return (
    <div className="ui-stack">
      <div className="ui-seg" role="group" aria-label="PTO view">
        {VIEWS.map(v => <Button key={v.key} variant="quiet" aria-pressed={view === v.key} onClick={() => setView(v.key)}>{v.label}</Button>)}
      </div>
      {view === 'employees' && <EmployeesView />}
      {view === 'calendar' && <CalendarView />}
      {view === 'approvals' && <ApprovalQueue />}
      {view === 'changes' && <ChangeRequestsView />}
      {view === 'holidays' && <HolidayCalendar />}
      {view === 'reports' && <ReportsView />}
    </div>
  );
}
